import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  MississippiCoverReviewDto,
  SaveMississippiCoverCorrectionsRequest,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, apiResponse } from './apiFetch';
import type { ApiErrorBody } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';

/**
 * The Mississippi in-app cover review: what the app pre-fills on the official cover, the treasurer's
 * stored corrections over it, and the values the cover will file. Corrections are stored on the report,
 * so every later generate — the download here and the send — files the corrected cover.
 */
export function useMississippiCoverReview(reportId: string | null) {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.reports.mississippiCover(reportId ?? ''),
    queryFn: () =>
      apiJson<MississippiCoverReviewDto>(`/api/finance/reports/${reportId}/mississippi-cover`, {
        // A JSON `null` error body still falls back to the generic message.
        error: (err: ApiErrorBody | null) => new Error(err?.error ?? err?.message ?? 'Failed to load the cover'),
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!reportId,
    // The derived figures move with the ledger; refetch on each open.
    staleTime: 0,
  });
}

/**
 * Store the treasurer's cover corrections. The body carries the report's concurrency token (a stale token
 * is a 409); the response is the refreshed review carrying the incremented token, written straight into the
 * cache so a rapid follow-up save sends the fresh token rather than false-conflicting with itself. The
 * report detail refreshes from the write set, which names the report whose token the write bumped.
 */
export function useSaveMississippiCoverCorrections(reportId: string) {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    // Serialize this report's cover saves; each carries the token the one before it returned.
    scope: { id: `mississippi-cover:${reportId}` },
    mutationFn: async ({
      reportId,
      data,
    }: {
      reportId: string;
      data: SaveMississippiCoverCorrectionsRequest;
    }): Promise<MississippiCoverReviewDto> => {
      const response = await apiResponse(`/api/finance/reports/${reportId}/mississippi-cover`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        json: data,
      });
      if (response.status === 409) {
        throw new Error('This report changed since you loaded it. Refresh and try again.');
      }
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.error ?? err?.message ?? 'Failed to save the cover corrections');
      }
      return response.json() as Promise<MississippiCoverReviewDto>;
    },
    onSuccess: (result, { reportId }) => {
      queryClient.setQueryData(queryKeys.finance.reports.mississippiCover(reportId), result);
    },
  });

  return { saveCoverCorrections: mutateAsync };
}
